import { AddIcon, RepeatIcon } from '@chakra-ui/icons';
import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Heading,
  IconButton,
  useDisclosure,
  Box,
  Image,
  Badge,
  Button,
  Switch,
} from '@chakra-ui/react';
import Tooltip from '../../../../../../../components/Tooltip';
import { useEffect, useState } from 'react';

import firebaseFirestore from '~/config/firebase/firestore';

import CompanyProfileModal from '../components/CompanyProfileModal';
import { collection, getDocs, doc, query, where, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import firebaseFunctions from '~/config/firebase/functions/functions';
import { Company } from '~/core/types';
import { toast } from 'react-toastify';

/**
 * Rehace el directorio de clientes de un negocio: la libreta que el buscador
 * del formulario usa para autocompletar.
 *
 * Se mantiene solo con cada reserva y se repasa entero cada domingo, así que
 * esto casi nunca hace falta. Casi: la primera vez hay que crearlo, y si
 * alguna vez se ve raro, este botón lo deja como debe estar.
 */
const reconstruirDirectorio = httpsCallable<
  { companyId: string },
  { ok: boolean; personas: number; partes: number }
>(firebaseFunctions, 'rebuildClientDirectory', {
  // Por defecto la llamada caduca a los 70 s. Releer las 11.000 reservas de
  // NOI tarda menos, pero no tan poco como para arriesgarse: la función tiene
  // 9 minutos, y aquí se espera lo mismo. Si no, el navegador daría error
  // mientras el servidor termina bien y nadie entendería nada.
  timeout: 540000,
});

const BusinessSettings = () => {
  const [companies, setCompanies] = useState<
    Array<{
      name: string;
      id: string;
      logoUrl: string;
      status: 'active' | 'inactive';
      type: string;
    }>
  >([]);
  const [company, setCompany] = useState<Partial<Company>>();
  const [selectedUser, setSelectedUser] = useState<any>();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getCompanies = async () => {
    const companiesCollection = collection(firebaseFirestore, 'companies');
    const companiesSnapshot = await getDocs(companiesCollection);
    // Se conservan todos los campos del negocio: al quedarse solo con unos
    // pocos, el formulario de edición no recibía el resto (por ejemplo los
    // dominios permitidos) y los mostraba vacíos.
    const companiesData = companiesSnapshot.docs.map((doc) => ({
      ...doc.data(),
      name: doc.data().name,
      id: doc.id,
      logoUrl: doc.data().logoUrl,
      type: doc.data().type,
      status: (doc.data().status as 'active' | 'inactive') || 'active', // Default to active if not set
    }));
    setCompanies(companiesData);
  };

  const getAdminUserByCompany = async (companyId: string) => {
    setSelectedUser(undefined);

    // El rol se busca por nombre, no por un id fijo: cada entorno tiene sus
    // propios identificadores y el que estaba escrito a mano solo existe en
    // desarrollo, así que en producción nunca encontraba al administrador.
    const rolesSnap = await getDocs(
      query(collection(firebaseFirestore, 'roles'), where('name', '==', 'admin'))
    );
    if (rolesSnap.empty) return;

    const companyRef = doc(firebaseFirestore, 'companies', companyId);
    const q = query(
      collection(firebaseFirestore, 'users_companies'),
      where('role', '==', rolesSnap.docs[0].ref),
      where('company', '==', companyRef)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return;
    }
    const userDocSnap = querySnapshot.docs[0].data();
    const user = await getDoc(userDocSnap.user);

    setSelectedUser(user.data());
  };

  const toggleCompanyStatus = async (companyId: string, currentStatus: 'active' | 'inactive') => {
    try {
      const newStatus: 'active' | 'inactive' = currentStatus === 'active' ? 'inactive' : 'active';
      const companyRef = doc(firebaseFirestore, 'companies', companyId);
      await updateDoc(companyRef, {
        status: newStatus,
        updatedAt: new Date(),
      });
      toast.success(`Empresa ${newStatus === 'active' ? 'activada' : 'desactivada'} correctamente`);
      await getCompanies(); // Refresh the list
    } catch (error) {
      console.error('Error updating company status:', error);
      toast.error('Error al actualizar el estado de la empresa');
    }
  };

  // Qué negocio se está reconstruyendo ahora mismo, para el indicador.
  const [reconstruyendo, setReconstruyendo] = useState<string>();

  const rehacerDirectorio = async (companyId: string, nombre: string) => {
    setReconstruyendo(companyId);
    try {
      const { data } = await reconstruirDirectorio({ companyId });
      toast.success(
        `Directorio de ${nombre}: ${data.personas.toLocaleString('es')} clientes.`
      );
    } catch (error) {
      const mensaje = (error as { message?: string })?.message;
      toast.error(mensaje || 'No se pudo reconstruir el directorio.');
    } finally {
      setReconstruyendo(undefined);
    }
  };

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <Flex flexDirection={'column'}>
      <Flex align='center' gap={4} mb={4}>
        <Heading size='md'>Negocios</Heading>
        <IconButton
          colorScheme='blue'
          size='xs'
          aria-label='Add User'
          icon={<AddIcon />}
          onClick={() => {
            setCompany(undefined);
            onOpen();
          }}
        />
      </Flex>

      <TableContainer>
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Id</Th>
              <Th>Nombre</Th>
              <Th>Logo</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {companies.map((company) => {
              return (
                <Tr
                  _hover={{ bg: 'gray.100', cursor: 'pointer' }}
                  onClick={async () => {
                    setCompany(company);
                    await getAdminUserByCompany(company.id);

                    onOpen();
                  }}
                  key={company.id}
                >
                  <Td>{company.id}</Td>
                  <Td>{company.name}</Td>
                  <Td>
                    <Box>
                      <Image boxSize='30px' objectFit='cover' src={company.logoUrl} />
                    </Box>
                  </Td>
                  <Td>
                    <Badge colorScheme={company.status === 'active' ? 'green' : 'red'}>
                      {company.status === 'active' ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex align='center' gap={3}>
                      <Tooltip label={company.status === 'active' ? 'Desactivar' : 'Activar'}>
                        {/* El Switch va envuelto a propósito: Chakra engancha
                            el tooltip al <input> oculto de 1x1 que hay dentro,
                            y el ratón nunca llega a "salir" de él, así que el
                            aviso se quedaba pegado. Con un elemento de verdad
                            alrededor, sale al apartar el ratón. */}
                        <Box display='inline-flex'>
                          <Switch
                            isChecked={company.status === 'active'}
                            onChange={async () => {
                              await toggleCompanyStatus(company.id, company.status);
                            }}
                          />
                        </Box>
                      </Tooltip>
                      <Tooltip label='Rehace la libreta de clientes que usa el buscador del formulario. Se mantiene sola; esto solo hace falta la primera vez o si algo se ve raro.'>
                        <Button
                          size='xs'
                          variant='outline'
                          leftIcon={<RepeatIcon />}
                          isLoading={reconstruyendo === company.id}
                          loadingText='Reconstruyendo'
                          // La fila entera abre el negocio: sin esto, pulsar
                          // aquí también abriría la ventana de edición.
                          onClick={(e) => {
                            e.stopPropagation();
                            rehacerDirectorio(company.id, company.name);
                          }}
                        >
                          Directorio
                        </Button>
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
      <CompanyProfileModal
        isOpen={isOpen}
        onClose={onClose}
        selectedUser={selectedUser}
        company={company}
      />
    </Flex>
  );
};

export default BusinessSettings;
